import { adjustmentTypesMapping, orderTypeConstants } from "constants/order";
import { removeClearPatientFee } from "helpers/fee";
import {
  parsePrice,
  centsToDollars,
  calculateDiscountPercent,
} from "helpers/price";
import { roundToX } from "helpers/round";
import { useOrder } from "hooks/useOrder";
import usePrice from "hooks/usePrice";
import { sizer } from "layout/styles/styled/sizer";
import { Label } from "layout/typography/Label";
import { isEmpty, sumBy } from "lodash";
import { addQuantityToRow } from "pages/CreateOrder/components/ServiceDetails/helpers";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { useMemo, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  toggleDisplayAdjustments,
  toggleDisplayPatientResponsibility,
  toggleDisplayProviderPayoutCalculations,
} from "store/slices/serviceDetails";
import styled from "styled-components";
import {
  IDefaultServiceDetails,
  IOrder,
  IPatientResponsibilityServiceDetails,
} from "types/Order/Order";
import { IProviderAmount } from "types/Payment";
import { SpecialtyList, SpecialtyListDict } from "types/Provider/Provider";

import PatientBenefits from "../PatientBenefits/PatientBenefits";
import { normalizePaymentPlanBreakdown } from "../ProviderPayoutCalculations/helpers";
import ProviderPayoutCalculations from "../ProviderPayoutCalculations/ProviderPayoutCalculations";

type ProviderRow = {
  code: string;
  cptDescription: string;
  amountInCents: number;
  specialty?: string;
  providerName?: string;
  quantity?: number;
};

const StyledCard = styled(Card)`
  margin-bottom: ${sizer(2)};
  border: 1px solid var(--gray-new-200) !important;
`;

const CardHeader = styled.div`
  border-bottom: 1 solid gray;
`;

const FieldsContainer = styled.div`
  display: flex;
  justify-content: end;
  margin-top: ${sizer(4)};
`;

const NcciBypassedBanner = styled.div`
  background-color: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 6px;
  padding: 0.75rem 1rem;
  margin-top: 0.5rem;
  font-size: 0.9rem;
  color: #6d4c00;
`;

const StyledDataTable = styled(DataTable)`
  margin-top: ${sizer(6)};

  tr {
    background: var(--gray-50);
  }

  span.p-column-title {
    font-weight: var(--font-weight-semibold);
    font-size: var(--fontsize-contents);
    color: var(--color-black-4);
  }

  td {
    font-weight: var(--font-weight-medium);
    font-size: var(--fontsize-contents);
    color: var(--color-matte-black);
  }
`;

const Discount = styled.div`
  text-align: end;
  color: var(--green-600);
  font-weight: var(--font-weight-semibold);
  font-size: var(--fontsize-contents);
  margin-right: var(--space-sm);
  padding-top: 0;
  padding-bottom: 0;

  @media (min-width: 720px) {
    text-align: center;
  }

  @media (min-width: 1020px) {
    text-align: end;
  }
`;

interface IServiceDetailsEdit {
  deductible: number;
  outOfPocketMax: number;
  flatCopay: number;
  coinsurancePercent: number;
  amountLeft: number;
}

function ServiceDetails({
  order,
  onUpdateOrder,
}: {
  order: IOrder;
  onUpdateOrder: () => void;
}) {
  const { setValue } = useFormContext<IServiceDetailsEdit>();
  const dispatch = useDispatch();
  const {
    displayAdjustments,
    displayPatientResponsibility,
    displayProviderPayoutCalculations,
  } = useSelector((state: any) => state.serviceDetailsSlice);

  const { getClearPriceByOrderType, insuranceAmounts, getInsuranceAmounts } =
    usePrice();
  const { isDefaultOrderType } = useOrder(order);
  const [showMore, setShowMore] = useState(false);

  const firstColumnTemplate = (row: ProviderRow) => {
    return <span className="font-bold">{row.providerName}</span>;
  };

  const firstColumnBold = (text: string) => {
    return <span className="font-bold">{text}</span>;
  };

  useEffect(() => {
    setValue(
      "deductible",
      centsToDollars(order?.gfeQuote?.remainingDeductibleInCents || 0)
    );
    setValue(
      "outOfPocketMax",
      centsToDollars(order?.gfeQuote?.outOfPocketMaxInCents || 0)
    );
    setValue(
      "flatCopay",
      centsToDollars(order?.gfeQuote?.flatCopayInCents || 0)
    );
    setValue("coinsurancePercent", order?.gfeQuote?.coinsurancePercent || 0);
  }, [order, setValue]);

  const columns = useMemo(() => {
    if (order.orderType === orderTypeConstants.PATIENT_RESPONSIBILITY) {
      return [
        { field: "code", header: "CPT Code" },
        { field: "cptDescription", header: "CPT Description" },
      ];
    }

    return [
      {
        field: "code",
        header: "CPT Code/Name",
        body: (row: ProviderRow) =>
          firstColumnBold(`${row.code} - ${row.cptDescription}`),
        headerStyle: { width: "30%" },
      },
      {
        field: "specialty",
        header: "Specialty",
        body: (row: ProviderRow) =>
          SpecialtyListDict[row.specialty as SpecialtyList],
        headerStyle: { width: "15%" },
      },
      {
        header: "Amount",
        field: "amountInCents",
        body: (row: ProviderRow) => {
          const quantity = row.quantity || 1;
          return parsePrice(centsToDollars(row.amountInCents * quantity));
        },
        headerStyle: { width: "15%" },
      },
      {
        field: "providerName",
        header: "Provider",
        headerStyle: { width: "25%" },
      },
      { field: "quantity", header: "Quantity", headerStyle: { width: "15%" } },
    ];
  }, [order]);

  const columnsAdjustmentAndPatientResponsibility = useMemo(() => {
    return [
      {
        field: "providerName",
        header: "Provider",
        body: (row: ProviderRow) => firstColumnTemplate(row),
      },
      {
        field: "code",
        header: "CPT Code/Name",
      },
      {
        field: "specialty",
        header: "Description",
      },
      {
        field: "amountInCents",
        header: "Amount",
        body: (row: ProviderRow) =>
          parsePrice(centsToDollars(row.amountInCents)),
      },
    ];
  }, [order]);

  const parseDataToPatientResponsibility = useMemo(() => {
    return (
      order.gfeQuote?.pricingBreakdown.procedureBreakdown?.map(
        (aPatientResponsibility) => {
          const { code, owedByPatient, providerName } = aPatientResponsibility;
          const { coinsurance, deductible, total, selfPay } = owedByPatient;

          let description = "";

          if (coinsurance > 0 && deductible > 0) {
            description = "Deductible + Coinsurance";
          } else if (coinsurance > 0) {
            description = "Coinsurance";
          } else if (deductible > 0) {
            description = "Deductible";
          } else if (selfPay > 0) {
            description = "Self-Pay";
          }
          return {
            code,
            amountInCents: total,
            specialty: description,
            providerName,
          };
        }
      ) || []
    );
  }, [order.gfeQuote]);

  const parseDataToAdjustment = useMemo(() => {
    return (
      order.gfeQuote?.pricingBreakdown.adjustments?.map(
        (aPatientResponsibility) => {
          const {
            procedure,
            discountType,
            discountedPrice,
            hierarchyOverridden,
          } = aPatientResponsibility;
          const { code, providerName } = procedure;
          const label = adjustmentTypesMapping[discountType] || discountType;

          return {
            code,
            amountInCents: discountedPrice,
            specialty: hierarchyOverridden ? `${label} (Overridden)` : label,
            providerName,
          };
        }
      ) || []
    ).sort((a, b) => {
      const aKey = `${a.providerName}-${a.code}`;
      const bKey = `${b.providerName}-${b.code}`;
      return aKey.localeCompare(bKey);
    });
  }, [order.gfeQuote]);

  const paymentBreakdownDetails = useMemo(() => {
    const list = order?.paymentFullBreakdown?.bundledProviderAmounts;

    const sortedList = list?.length
      ? [...list].sort((a, b) => b.providerPriority - a.providerPriority)
      : [];

    // Transform IProviderAmount to ProviderAmountRow
    return sortedList.map((item: IProviderAmount) => ({
      ...item,
      patientPortion: item.patientPortion || 0, // Provide a default value or calculate
      remainingToPayProvider: item.remainingToPayProvider || 0, // Provide a default value or calculate
    }));
  }, [order]);

  const clearFee = useMemo(() => {
    return order?.paymentFullBreakdown?.clearFeeAmountInCents;
  }, [order]);

  const payments = order.paymentPlanBreakdown
    ? normalizePaymentPlanBreakdown(order.paymentPlanBreakdown.payments)
    : undefined;

  // Extract splitAmountsByProvider from both breakdowns
  const paymentPlanSplitAmountsByProvider = useMemo(() => {
    return (order.paymentPlanBreakdown as any)?.splitAmountsByProvider;
  }, [order.paymentPlanBreakdown]);

  const fullBreakdownSplitAmountsByProvider = useMemo(() => {
    return (order.paymentFullBreakdown as any)?.splitAmountsByProvider;
  }, [order.paymentFullBreakdown]);

  useEffect(() => {
    if (!isEmpty(order.gfeQuote)) {
      getInsuranceAmounts(order.gfeQuote.pricingBreakdown);
    }
  }, [order.gfeQuote]);

  const paymentInfo = useMemo(() => {
    const { total, clearPrice } = getClearPriceByOrderType({ order });

    const subtotalValue = sumBy(
      order.serviceDetails.procedures as any,
      "amountInCents"
    );

    const subTotal = isDefaultOrderType ? subtotalValue : total;
    const totalWithDiscount = clearPrice;

    // We calculate Clear Patient Fee using the percentage saved in the order table
    let clearPatientFee = 0;
    let clearPatientFeeInDollars = 0;

    // For GFE orders, patientResponsibilityEstimateAmountInCents now includes the Clear Patient Fee, when it exists
    // So we need to calculate the subtotal by subtracting the Clear Patient Fee
    if (
      order.orderType === orderTypeConstants.GFE &&
      order.clearPatientFeeValueInPercentage &&
      order.clearPatientFeeValueInPercentage > 0
    ) {
      // Convert to dollars, remove fee with rounding logic, convert back
      const totalInDollars = centsToDollars(total);
      const subtotalInDollars = removeClearPatientFee(
        totalInDollars,
        order.clearPatientFeeValueInPercentage
      );
      const subtotalAmount = subtotalInDollars * 100; // back to cents for internal calc
      clearPatientFee = total - subtotalAmount;
      clearPatientFeeInDollars = centsToDollars(clearPatientFee);
    }

    // For GFE orders, calculate the display values
    let rawTotal;
    let rawTotalWithPatientFee;

    if (order.orderType === orderTypeConstants.GFE && clearPatientFee > 0) {
      // total field contains the total including Clear Patient Fee
      rawTotalWithPatientFee = parsePrice(centsToDollars(total));
      // Calculate subtotal by subtracting Clear Patient Fee
      rawTotal = parsePrice(centsToDollars(total - clearPatientFee));
    } else {
      // For non-GFE orders or GFE orders without Clear Patient Fee
      rawTotal = parsePrice(centsToDollars(subTotal));
      // rawTotalWithPatientFee not needed since we don't display it in this case
    }

    const rawTotalWithDiscount = parsePrice(centsToDollars(totalWithDiscount));

    let providerFinalAmount;
    if (order.orderType === orderTypeConstants.PATIENT_RESPONSIBILITY) {
      const providerAmountInCents =
        order?.paymentFullBreakdown?.patientRespFacilityAmountInCents;
      providerFinalAmount =
        providerAmountInCents &&
        parsePrice(centsToDollars(providerAmountInCents));
    }

    const discountPercentForPatientResponsibility = roundToX(
      calculateDiscountPercent(total, totalWithDiscount),
      2
    );

    return {
      rawTotalWithDiscount,
      rawTotal,
      rawTotalWithPatientFee,
      clearPatientFeeInDollars,
      discountPercentForPatientResponsibility,
      providerFinalAmount,
    };
  }, [order, order.serviceDetails, order.serviceDetails.procedures]);

  const orderTypeRules = useMemo(() => {
    const isGFEorPatientResponsibility =
      order.orderType === orderTypeConstants.PATIENT_RESPONSIBILITY ||
      order.orderType === orderTypeConstants.GFE;
    const isPatientResponsibility =
      order.orderType === orderTypeConstants.PATIENT_RESPONSIBILITY;
    const isGFE = order.orderType === orderTypeConstants.GFE;

    return { isGFEorPatientResponsibility, isPatientResponsibility, isGFE };
  }, [order.orderType]);

  const subtotalLabel = useMemo(() => {
    if (orderTypeRules.isGFEorPatientResponsibility) {
      if (orderTypeRules.isGFE && paymentInfo.clearPatientFeeInDollars > 0) {
        return "Subtotal estimated patient cost";
      }
      return "Total estimated patient cost";
    }
    return "Subtotal Cost";
  }, [
    orderTypeRules.isGFEorPatientResponsibility,
    orderTypeRules.isGFE,
    paymentInfo.clearPatientFeeInDollars,
  ]);

  return (
    <>
      {orderTypeRules.isGFE && <PatientBenefits order={order} />}
      <StyledCard title="Service Details">
        <StyledDataTable
          rowGroupMode="rowspan"
          style={{ marginLeft: "-1rem", marginRight: "-1rem" }}
          groupRowsBy="code"
          value={
            orderTypeRules.isPatientResponsibility
              ? order.serviceDetails.procedures
              : addQuantityToRow(order.serviceDetails.procedures)
          }
        >
          {columns.map((col: any) => (
            <Column
              key={col.field}
              field={col.field}
              header={col.header}
              body={col?.body}
              headerStyle={col.headerStyle}
            />
          ))}
        </StyledDataTable>
        {orderTypeRules.isGFE && (
          <>
            <div>
              <CardHeader
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  flexDirection: "row",
                  alignItems: "center",
                  paddingTop: sizer(5),
                }}
                onClick={() => {
                  dispatch(toggleDisplayAdjustments());
                }}
              >
                <span
                  style={{
                    fontSize: "1.1rem",
                    fontWeight: 700,
                  }}
                >
                  Adjustments
                </span>
                {displayAdjustments ? (
                  <i
                    className="pi pi-angle-up
"
                    style={{ fontSize: "1.5rem" }}
                  />
                ) : (
                  <i
                    className="pi pi-angle-down
"
                    style={{ fontSize: "1.5rem" }}
                  />
                )}
              </CardHeader>
              {displayAdjustments && (
                <StyledDataTable
                  rowGroupMode="rowspan"
                  style={{ marginLeft: "-1rem", marginRight: "-1rem" }}
                  emptyMessage="No adjustments"
                  value={parseDataToAdjustment}
                >
                  {columnsAdjustmentAndPatientResponsibility.map((col) => (
                    <Column
                      key={col.field}
                      field={col.field}
                      body={col?.body}
                      header={col?.header}
                    />
                  ))}
                </StyledDataTable>
              )}
              {displayAdjustments &&
                (order.gfeQuote as any)?.excludedNcciRulesetIds?.length > 0 && (
                  <NcciBypassedBanner>
                    <i
                      className="pi pi-info-circle"
                      style={{ marginRight: "0.4rem" }}
                    />
                    <strong>NCCI Adjustments Bypassed</strong>
                    <span style={{ marginLeft: "0.5rem" }}>
                      {(order.gfeQuote as any).excludedNcciRulesetIds.length}{" "}
                      NCCI rule
                      {(order.gfeQuote as any).excludedNcciRulesetIds.length > 1
                        ? "s were"
                        : " was"}{" "}
                      bypassed when this estimate was created. The adjustments
                      above reflect the recalculated pricing.
                    </span>
                  </NcciBypassedBanner>
                )}
            </div>
            <div>
              <CardHeader
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  flexDirection: "row",
                  alignItems: "center",
                  paddingTop: sizer(5),
                }}
                onClick={() => {
                  dispatch(toggleDisplayPatientResponsibility());
                }}
              >
                <span
                  style={{
                    fontSize: "1.1rem",
                    fontWeight: 700,
                  }}
                >
                  Patient Responsibility
                </span>
                {displayPatientResponsibility ? (
                  <i
                    className="pi pi-angle-up
"
                    style={{ fontSize: "1.5rem" }}
                  />
                ) : (
                  <i
                    className="pi pi-angle-down
"
                    style={{ fontSize: "1.5rem" }}
                  />
                )}
              </CardHeader>
              {displayPatientResponsibility && (
                <StyledDataTable
                  rowGroupMode="rowspan"
                  style={{ marginLeft: "-1rem", marginRight: "-1rem" }}
                  emptyMessage="No Patient Responsibility"
                  value={parseDataToPatientResponsibility}
                >
                  {columnsAdjustmentAndPatientResponsibility.map((col) => (
                    <Column
                      key={col.field}
                      field={col.field}
                      body={col?.body}
                      header={col?.header}
                    />
                  ))}
                </StyledDataTable>
              )}
            </div>
          </>
        )}
        {orderTypeRules.isGFEorPatientResponsibility && (
          <div>
            <CardHeader
              style={{
                display: "flex",
                justifyContent: "space-between",
                flexDirection: "row",
                alignItems: "center",
                paddingTop: sizer(5),
              }}
              onClick={() => {
                dispatch(toggleDisplayProviderPayoutCalculations());
              }}
            >
              <span
                style={{
                  fontSize: "1.1rem",
                  fontWeight: 700,
                }}
              >
                Provider Payout Calculations
              </span>
              <i
                className={
                  displayProviderPayoutCalculations
                    ? "pi pi-angle-up"
                    : "pi pi-angle-down"
                }
                style={{ fontSize: "1.5rem" }}
              />
            </CardHeader>
            {displayProviderPayoutCalculations && (
              <ProviderPayoutCalculations
                providerAmounts={paymentBreakdownDetails}
                clearFee={clearFee}
                payments={payments}
                fullBreakdownSplitPaymentDetails={
                  order?.paymentFullBreakdown?.splitPaymentDetails
                }
                paymentPlanSplitPaymentDetails={
                  order?.paymentPlanBreakdown?.splitPaymentDetails
                }
                orderStatus={order.status}
                orderId={order.id}
                onUpdateOrder={onUpdateOrder}
                clearPatientFeeValueInPercentage={
                  order.clearPatientFeeValueInPercentage
                }
                paymentPlanSplitAmountsByProvider={
                  paymentPlanSplitAmountsByProvider
                }
                fullBreakdownSplitAmountsByProvider={
                  fullBreakdownSplitAmountsByProvider
                }
                orderType={order.orderType}
                paymentPlan={order?.paymentPlan?.find((p) => p.active)}
              />
            )}
          </div>
        )}
        <div>
          {orderTypeRules.isPatientResponsibility && (
            <FieldsContainer>
              <div className="flex align-items-center">
                <label
                  htmlFor="totalInsuranceAllowable"
                  className="mr-2 flex align-items-center"
                >
                  Total Insurance Allowable
                </label>

                <InputText
                  disabled
                  id="totalInsuranceAllowable"
                  aria-labelledby="totalInsuranceAllowable"
                  value={
                    (
                      order?.serviceDetails as IPatientResponsibilityServiceDetails
                    )?.totalInsuranceAllowableInCents == null
                      ? ""
                      : parsePrice(
                          centsToDollars(
                            (
                              order?.serviceDetails as IPatientResponsibilityServiceDetails
                            )?.totalInsuranceAllowableInCents || 0
                          )
                        )
                  }
                />
              </div>
            </FieldsContainer>
          )}
          {orderTypeRules.isGFE && (
            <>
              <FieldsContainer>
                <div className="flex align-items-center">
                  <label
                    htmlFor="totalInsuranceAllowable"
                    className="mr-2 flex align-items-center"
                  >
                    Total Insurance Allowable
                  </label>

                  <InputText
                    disabled
                    id="totalInsuranceAllowable"
                    aria-labelledby="totalInsuranceAllowable"
                    value={parsePrice(
                      centsToDollars(
                        insuranceAmounts?.totalAllowedInsuranceAmount
                      )
                    )}
                  />
                </div>
              </FieldsContainer>
              {showMore && (
                <FieldsContainer>
                  <div className="flex align-items-center">
                    <label
                      htmlFor="totalInsurancePortion"
                      className="mr-2 flex align-items-center"
                    >
                      Total Insurance Portion
                    </label>

                    <InputText
                      disabled
                      id="totalInsurancePortion"
                      aria-labelledby="totalInsurancePortion"
                      value={parsePrice(
                        centsToDollars(
                          insuranceAmounts?.totalInsurancePayInCents
                        )
                      )}
                    />
                  </div>
                </FieldsContainer>
              )}
            </>
          )}
          {!orderTypeRules.isGFEorPatientResponsibility && (
            <FieldsContainer>
              <div className="flex align-items-center">
                <Label htmlFor="estimatedVisitCost">
                  Facility Patient Estimate
                </Label>

                <InputText
                  disabled
                  id="estimatedVisitCost"
                  aria-labelledby="estimatedVisitCost"
                  value={parsePrice(
                    centsToDollars(
                      (order.serviceDetails as IDefaultServiceDetails)
                        .estimatedVisitCost
                    )
                  )}
                />
              </div>
            </FieldsContainer>
          )}
          {(((orderTypeRules.isGFE ||
            !orderTypeRules.isGFEorPatientResponsibility) &&
            showMore) ||
            orderTypeRules.isPatientResponsibility) && (
            <FieldsContainer>
              <div className="flex align-items-center">
                <label
                  htmlFor="subtotal"
                  className="mr-2 flex align-items-center"
                >
                  {subtotalLabel}
                </label>

                <InputText
                  disabled
                  id="subtotal"
                  aria-labelledby="subtotal"
                  value={paymentInfo.rawTotal}
                />
              </div>
            </FieldsContainer>
          )}
          {orderTypeRules.isGFE &&
            paymentInfo.clearPatientFeeInDollars > 0 &&
            showMore && (
              <FieldsContainer>
                <div className="flex align-items-center">
                  <label className="mr-2 flex align-items-center">
                    Clear Patient Fee ({order.clearPatientFeeValueInPercentage}
                    %)
                  </label>
                  <InputText
                    disabled
                    value={parsePrice(paymentInfo.clearPatientFeeInDollars)}
                  />
                </div>
              </FieldsContainer>
            )}

          {orderTypeRules.isGFE &&
            paymentInfo.clearPatientFeeInDollars > 0 &&
            showMore && (
              <FieldsContainer>
                <div className="flex align-items-center">
                  <label className="mr-2 flex align-items-center">
                    Total estimated patient cost
                  </label>
                  <InputText
                    disabled
                    value={paymentInfo.rawTotalWithPatientFee}
                  />
                </div>
              </FieldsContainer>
            )}

          <FieldsContainer>
            <div className="flex align-items-center">
              <label
                htmlFor="totalPrice"
                className="mr-2 flex align-items-center"
              >
                {orderTypeRules.isGFEorPatientResponsibility
                  ? "Clear price offered to patient"
                  : "Clear Price for Patient"}
              </label>

              <InputText
                disabled
                id="totalPrice"
                aria-labelledby="totalPrice"
                value={paymentInfo.rawTotalWithDiscount}
              />
            </div>
          </FieldsContainer>

          {orderTypeRules.isGFEorPatientResponsibility && showMore && (
            <Discount className="mt-3 mr-0">
              {paymentInfo.discountPercentForPatientResponsibility
                ? `${paymentInfo.discountPercentForPatientResponsibility}%
              discount`
                : ""}
            </Discount>
          )}
          {paymentInfo.providerFinalAmount && showMore && (
            <FieldsContainer>
              <div className="flex align-items-center">
                <label
                  htmlFor="finalPayment"
                  className="mr-2 flex align-items-center"
                >
                  Provider final payment
                </label>

                <InputText
                  disabled
                  id="finalPayment"
                  aria-labelledby="finalPayment"
                  value={paymentInfo.providerFinalAmount}
                />
              </div>
            </FieldsContainer>
          )}
          <FieldsContainer>
            <Button
              label={showMore ? "Show Less" : "Show More"}
              onClick={() => setShowMore(!showMore)}
              icon={showMore ? "pi pi-chevron-up" : "pi pi-chevron-down"}
              className="p-button-text p-0"
            />
          </FieldsContainer>
        </div>
      </StyledCard>
    </>
  );
}

export default ServiceDetails;
